import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { AlertContent } from 'components';
import { FailToastContainer } from 'components/Toast/ToastContainer';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { linkEmployeeRequest, TipCalculator } from 'store/TipCalculator';
import { IStore } from 'store/types';
import { DisbursementMethod, EmployeeType, IEmployee, IEmployerSite, IJob, SiteRole } from 'types';

interface IAddToSite {
  role: string;
  job: IJob;
  disbursementMethod: string;
  employeeId?: string;
  integration: { id: string; siteId: string; type: string };
}

interface IGetOptionsProps {
  employee?: IEmployee;
  email?: string;
  site: IEmployerSite;
  isDuplicateIntegration: boolean;
  onClose: () => void;
  linkUser(employee: IEmployee): void;
}

export const getDuplicateUserAlertOptions = ({ employee, email, site, onClose, linkUser, isDuplicateIntegration }: IGetOptionsProps) => {
  let title = 'User already exists';
  let message;
  let rightActionLabel = '';
  let onSubmit = onClose;
  let hideLeftAction = false;

  if (employee) {
    // Do not allow linking of org admins
    if (employee.type === EmployeeType.ADMIN) {
      message = (
        <>
          <b>{email || employee.inviteRecipient}</b> {' is already being used by the organization admin '} <b>{employee.name}</b>.<br />
          <br />
          <b>Organization admins cannot be linked.</b>
        </>
      );
      rightActionLabel = 'Close';
      hideLeftAction = true;
    } else if (site) {
      // get employee site role
      const employeeSite = employee.sites?.find((s) => s.id === site.id);

      // check if there is a duplicate employee associated with the organization
      if (isDuplicateIntegration) {
        title = 'Employee already linked';
        message = (
          <>
            <b>{email || employee.inviteRecipient}</b> {' is already linked to'} <b>{employee.name}</b>.<br />
            <br />
            <b>Employees can only be linked once per site.</b>
          </>
        );
        hideLeftAction = true;
        rightActionLabel = 'Close';
      } else if (employee.status === 'dismissed') {
        title = 'Dismissed user found';
        message = (
          <>
            <b>{email || employee.inviteRecipient}</b> {' is already being used by dismissed user '} <b>{employee.name}</b>. Would you like to activate this existing user at <b>{site.name}</b>?
          </>
        );
        rightActionLabel = 'Activate Existing User';
        onSubmit = () => linkUser(employee);
      } else if (employeeSite) {
        if (employeeSite.status !== 'dismissed') {
          message = (
            <>
              <b>{email || employee.inviteRecipient}</b> {' is already being used by another user '} <b>{employee.name}</b>. Would you like to link to the existing user?
            </>
          );
          rightActionLabel = 'Link Existing User';
          onSubmit = () => linkUser(employee);
        } else {
          title = 'Dismissed user found';
          message = (
            <>
              <b>{email || employee.inviteRecipient}</b> {' is already being used by the dismissed user '} <b>{employee.name}</b>. Would you like to activate this existing user at <b>{site.name}</b>?
            </>
          );
          rightActionLabel = 'Activate Existing User';
          onSubmit = () => linkUser(employee);
        }
      } else {
        message = (
          <>
            <b>{email || employee.inviteRecipient}</b> {' is already being used by '} <b>{employee.name}</b> from this organization. Would you like to add & link the user to <b>{site.name}</b>?
          </>
        );
        rightActionLabel = 'Add & Link User';
        onSubmit = () => linkUser(employee);
      }
    }
  }

  return {
    title,
    message,
    rightActionLabel,
    hideLeftAction,
    onSubmit,
  };
};

interface IDuplicateUserAlertProps {
  employee: IEmployee;
  email?: string;
  values?: IAddToSite;
  site: IEmployerSite;
  onClose(): void;
  onPrimaryAction?: (employee: IEmployee) => void;
}

const DuplicateUserAlert = ({ employee, email, values, site, onClose, onPrimaryAction }: IDuplicateUserAlertProps) => {
  const dispatch = useDispatch();
  const isLinking = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, TipCalculator.LINK_EMPLOYEE_REQUEST));

  const linkUser = (employee: IEmployee) => {
    const onError = () => {
      toast(<FailToastContainer title="Error" message="Unable to link user" />);
    };

    const onSuccess = (employee: IEmployee) => {
      onPrimaryAction(employee);
    };

    const siteRole = { id: site.id, role: SiteRole.EMPLOYEE, job: values.job, disbursementMethod: values.disbursementMethod as DisbursementMethod, ...(values.employeeId && { employeeId: values.employeeId }) };
    dispatch(linkEmployeeRequest(employee, values.integration, siteRole, onSuccess, onError));
  };

  // check if employee is already linked to this integration but to a different pos employee
  const isDuplicateIntegration = employee?.integrations.some(
    (integration) => integration.id !== values.integration.id && integration.siteId === values.integration.siteId && integration.type.trim().toLowerCase() === values.integration.type.trim().toLowerCase()
  );

  const { title, message, rightActionLabel, onSubmit, hideLeftAction } = getDuplicateUserAlertOptions({ employee, email, site, onClose, linkUser, isDuplicateIntegration });

  return <AlertContent title={title} message={message} onLeftAction={onClose} onRightAction={onSubmit} leftActionLabel="Cancel" hideLeftAction={hideLeftAction} rightActionLabel={rightActionLabel} isLoading={isLinking} maxWidth={440} />;
};

export default DuplicateUserAlert;
